import { useRef, useState, useCallback, useEffect } from "react";
import * as THREE from "three";
import { useThree } from "@react-three/fiber";

interface EditorSliderProps {
  /** 슬라이더 축 방향 */
  axis: "x" | "y" | "z";
  /** 현재 값 */
  value: number;
  /** 최소값 */
  min: number;
  /** 최대값 */
  max: number;
  /** 값 변경 콜백 */
  onChange: (value: number) => void;
  /** 슬라이더 시작 위치 (월드 좌표) */
  position: [number, number, number];
  /** 슬라이더 길이 */
  length?: number;
  /** 활성화 여부 */
  enabled?: boolean;
  /** 슬라이더 색상 */
  color?: string;
  /** 마커 색상 */
  markerColor?: string;
  /** 마커 모양: cube (position용) | sphere (scale용) */
  markerShape?: "cube" | "sphere";
}

/**
 * 3D 슬라이더 컴포넌트
 * 그리드 옆에 배치되어 드래그로 값 조절
 * - cube 마커: 위치 조절용
 * - sphere 마커: 크기 조절용
 */
export function EditorSlider({
  axis,
  value,
  min,
  max,
  onChange,
  position,
  length = 2,
  enabled = true,
  color = "#666666",
  markerColor = "#4a90d9",
  markerShape = "sphere",
}: EditorSliderProps) {
  const { gl } = useThree();
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const markerRef = useRef<THREE.Mesh>(null);
  const startMouseRef = useRef<{ x: number; y: number } | null>(null);
  const startValueRef = useRef<number>(0);

  // 값을 0~1 범위로 정규화
  const normalizedValue = max > min ? (value - min) / (max - min) : 0;
  
  // 축에 따른 방향 벡터
  const direction: [number, number, number] = 
    axis === "x" ? [1, 0, 0] : 
    axis === "y" ? [0, 1, 0] : 
    [0, 0, 1];

  // 슬라이더 트랙 끝점 계산
  const trackEnd: [number, number, number] = [
    position[0] + direction[0] * length,
    position[1] + direction[1] * length,
    position[2] + direction[2] * length,
  ];

  // 마커 위치 계산
  const markerPosition: [number, number, number] = [
    position[0] + direction[0] * length * normalizedValue,
    position[1] + direction[1] * length * normalizedValue,
    position[2] + direction[2] * length * normalizedValue,
  ];

  // 스크린 좌표를 NDC로 변환
  const screenToNdc = useCallback(
    (screenX: number, screenY: number) => {
      const rect = gl.domElement.getBoundingClientRect();
      const ndcX = ((screenX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((screenY - rect.top) / rect.height) * 2 + 1;
      return { x: ndcX, y: ndcY };
    },
    [gl]
  );

  // 드래그 시작
  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!enabled) return;
      e.stopPropagation();
      
      setIsDragging(true);
      startMouseRef.current = { x: e.clientX, y: e.clientY };
      startValueRef.current = value;
      
      gl.domElement.setPointerCapture(e.nativeEvent.pointerId);
    },
    [enabled, value, gl]
  );

  // 드래그 중
  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      if (!isDragging || !startMouseRef.current || !enabled) return;

      const deltaX = e.clientX - startMouseRef.current.x;
      const deltaY = e.clientY - startMouseRef.current.y;
      
      // Oblique 투영에서 마우스 이동 → 축별 값 변환
      let mouseDelta: number;
      const sensitivity = 0.005;
      
      if (axis === "x") {
        // X축: 오른쪽 위 방향
        mouseDelta = (deltaX - deltaY) * sensitivity;
      } else if (axis === "y") {
        // Y축: 위 방향
        mouseDelta = -deltaY * sensitivity;
      } else {
        // Z축: 오른쪽 아래 방향 (부호 반전하여 직관적으로)
        mouseDelta = -(deltaX + deltaY) * sensitivity;
      }

      // 새 값 계산 및 클램핑
      const range = max - min;
      const newValue = Math.max(min, Math.min(max, startValueRef.current + mouseDelta * range));
      onChange(newValue);
    },
    [isDragging, enabled, axis, min, max, onChange]
  );

  // 드래그 종료
  const handlePointerUp = useCallback(
    (e: PointerEvent) => {
      if (!isDragging) return;
      
      setIsDragging(false);
      startMouseRef.current = null;
      
      gl.domElement.releasePointerCapture(e.pointerId);
    },
    [isDragging, gl]
  );

  // 문서 레벨 이벤트 리스너
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);

    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [enabled, handlePointerMove, handlePointerUp]);

  // 축 색상
  const axisColors = {
    x: "#ff6b6b",
    y: "#6bff6b", 
    z: "#6b6bff",
  };

  return (
    <group>
      {/* 슬라이더 트랙 (막대) */}
      <line>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={2}
            array={new Float32Array([
              position[0], position[1], position[2],
              trackEnd[0], trackEnd[1], trackEnd[2],
            ])}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial color={color} linewidth={2} />
      </line>

      {/* 시작점 마커 */}
      <mesh position={position}>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshBasicMaterial color={axisColors[axis]} />
      </mesh>

      {/* 끝점 마커 */}
      <mesh position={trackEnd}>
        <sphereGeometry args={[0.04, 8, 8]} />
        <meshBasicMaterial color={axisColors[axis]} />
      </mesh>

      {/* 드래그 가능한 마커 */}
      <mesh
        ref={markerRef}
        position={markerPosition}
        onPointerDown={handlePointerDown}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsDragging || setIsHovered(false)}
      >
        {/* 마커 모양: cube (position) vs sphere (scale) */}
        {markerShape === "cube" ? (
          <boxGeometry args={[0.18, 0.18, 0.18]} />
        ) : (
          <sphereGeometry args={[0.12, 16, 16]} />
        )}
        <meshStandardMaterial
          color={isDragging ? "#ffffff" : isHovered ? "#7ab3e8" : markerColor}
          emissive={isDragging ? markerColor : isHovered ? markerColor : "#1a4a7a"}
          emissiveIntensity={isDragging ? 0.8 : isHovered ? 0.5 : 0.2}
        />
      </mesh>

    
    </group>
  );
}

